/**
 * ZENTRA Calendar page — Gantt-style timeline per property.
 * Rows = properties, columns = days. Cells show booking bars, task chips, incident dots.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
    CalendarDays,
    RefreshCw,
    Plus,
    LinkIcon,
    Building2,
    AlertOctagon,
    Sparkles,
    LogOut,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import StateBadge from "@/components/StateBadge";
import {
    TASK_TYPE_LABELS,
    TASK_STATE_LABELS,
    PRIORITY_LABELS,
    INCIDENT_TYPE_LABELS,
} from "@/lib/dictionaries";

const SOURCE_COLOR = {
    airbnb: "#FF5A5F",
    booking: "#003580",
    vrbo: "#3B5998",
    avaibook: "#0EA5E9",
    avantio: "#7C3AED",
    manual: "#6F8F72",
    other: "#6F7470",
};

const SOURCE_LABEL = {
    airbnb: "Airbnb",
    booking: "Booking.com",
    vrbo: "Vrbo",
    avaibook: "Avaibook",
    avantio: "Avantio",
    manual: "Manual",
    other: "Otro",
};

const DAY_MS = 86400000;
const todayDate = () => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d;
};
const ymd = (d) => d.toISOString().slice(0, 10);
const parseYMD = (s) => {
    const [y, m, dy] = s.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, dy));
};
const fmtMonth = (d) =>
    d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
const fmtShortDay = (d) =>
    d.toLocaleDateString("es-ES", { weekday: "short" }).slice(0, 1).toUpperCase();
const fmtDayNum = (d) => d.getUTCDate();

export default function Calendar() {
    const [start, setStart] = useState(() => {
        const t = todayDate();
        t.setUTCDate(t.getUTCDate() - 2);
        return t;
    });
    const [days, setDays] = useState(30);
    const [properties, setProperties] = useState([]);
    const [clients, setClients] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showFeedModal, setShowFeedModal] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [scheduler, setScheduler] = useState(null);

    const loadScheduler = useCallback(async () => {
        try {
            const { data } = await api.get("/ical/scheduler-status");
            setScheduler(data);
        } catch (e) {
            /* ignore */
        }
    }, []);

    const range = useMemo(() => {
        const arr = [];
        for (let i = 0; i < days; i++) {
            const d = new Date(start.getTime() + i * DAY_MS);
            arr.push(d);
        }
        return arr;
    }, [start, days]);

    const fromISO = ymd(range[0]);
    const toISO = ymd(range[range.length - 1]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [props, cal, cli] = await Promise.all([
                api.get("/properties"),
                api.get(`/calendar?from_=${fromISO}&to=${toISO}`),
                api.get("/clients").catch(() => ({ data: [] })),
            ]);
            setProperties(props.data || []);
            setData(cal.data);
            setClients(cli.data || []);
        } finally {
            setLoading(false);
        }
    }, [fromISO, toISO]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        loadScheduler();
        const t = setInterval(loadScheduler, 60_000);
        return () => clearInterval(t);
    }, [loadScheduler]);

    const syncAll = async () => {
        setRefreshing(true);
        try {
            await api.post("/ical/sync-all");
            await load();
            await loadScheduler();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setRefreshing(false);
        }
    };

    const shift = (deltaDays) => {
        const d = new Date(start.getTime() + deltaDays * DAY_MS);
        setStart(d);
    };

    const goToday = () => {
        const t = todayDate();
        t.setUTCDate(t.getUTCDate() - 2);
        setStart(t);
    };

    // Group events by property
    const eventsByProperty = useMemo(() => {
        if (!data) return {};
        const out = {};
        for (const p of properties) out[p.id] = { bookings: [], tasks: [], incidents: [] };
        for (const b of data.bookings || []) {
            if (out[b.property_id]) out[b.property_id].bookings.push(b);
        }
        for (const t of data.tasks || []) {
            if (out[t.property_id]) out[t.property_id].tasks.push(t);
        }
        for (const i of data.incidents || []) {
            if (out[i.property_id]) out[i.property_id].incidents.push(i);
        }
        return out;
    }, [data, properties]);

    return (
        <div className="space-y-6" data-testid="calendar-page">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Centro temporal
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Calendario
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Reservas (Airbnb / Booking), limpiezas e incidencias en una sola vista.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={() => setShowFeedModal(true)}
                        data-testid="open-feed-modal"
                    >
                        <LinkIcon size={14} /> Suscribirse (.ics)
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={syncAll}
                        disabled={refreshing}
                        data-testid="sync-all-button"
                    >
                        <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                        {refreshing ? "Sincronizando…" : "Sincronizar iCal"}
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-primary"
                        onClick={() => setShowBookingModal(true)}
                        data-testid="new-booking-button"
                    >
                        <Plus size={14} /> Reserva manual
                    </button>
                </div>
            </div>

            {/* Toolbar */}
            <div className="zentra-card-static p-3 flex items-center gap-3 flex-wrap" data-testid="calendar-toolbar">
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        className="zentra-btn-ghost px-2.5"
                        onClick={() => shift(-7)}
                        data-testid="cal-prev"
                        aria-label="Semana anterior"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-ghost px-3"
                        onClick={goToday}
                        data-testid="cal-today"
                    >
                        Hoy
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-ghost px-2.5"
                        onClick={() => shift(7)}
                        data-testid="cal-next"
                        aria-label="Semana siguiente"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
                <div className="flex items-center gap-2 ml-1">
                    <CalendarDays size={14} className="text-zentra-muted" />
                    <span className="font-display font-semibold text-zentra-ink capitalize">
                        {fmtMonth(range[Math.floor(range.length / 2)])}
                    </span>
                    <span className="text-xs text-zentra-muted">
                        ({range[0].toLocaleDateString("es-ES", { day: "numeric", month: "short" })} →{" "}
                        {range[range.length - 1].toLocaleDateString("es-ES", { day: "numeric", month: "short" })})
                    </span>
                </div>
                <div className="ml-auto flex items-center gap-3 text-xs">
                    <SchedulerChip scheduler={scheduler} onRefresh={loadScheduler} />
                    <select
                        className="zentra-select w-auto py-1"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        data-testid="cal-range-select"
                    >
                        <option value={7}>1 semana</option>
                        <option value={14}>2 semanas</option>
                        <option value={30}>1 mes</option>
                    </select>
                </div>
            </div>

            {/* Legend (channel icons) */}
            <div className="flex items-center gap-3 flex-wrap text-xs text-zentra-muted" data-testid="calendar-legend">
                <ChannelIcon source="airbnb" size="sm" />
                <span>Airbnb</span>
                <ChannelIcon source="booking" size="sm" />
                <span>Booking.com</span>
                <ChannelIcon source="vrbo" size="sm" />
                <span>Vrbo</span>
                <ChannelIcon source="avaibook" size="sm" />
                <span>Avaibook</span>
                <ChannelIcon source="avantio" size="sm" />
                <span>Avantio</span>
                <ChannelIcon source="manual" size="sm" />
                <span>Manual / bloqueo</span>
                <span className="flex items-center gap-1.5 ml-3">
                    <Sparkles size={11} className="text-zentra-accent-strong" /> Limpieza
                </span>
                <span className="flex items-center gap-1.5">
                    <AlertOctagon size={11} className="text-red-600" /> Incidencia
                </span>
            </div>

            {/* Gantt — Avaibook-style header (3 rows) + single-row bookings per property */}
            {loading ? (
                <div className="zentra-skeleton h-96" />
            ) : properties.length === 0 ? (
                <EmptyState
                    icon={Building2}
                    title="Sin propiedades"
                    description="Añade propiedades para empezar a usar el calendario."
                />
            ) : (
                <div className="zentra-card-static overflow-x-auto" data-testid="calendar-gantt">
                    <CalendarHeader range={range} days={days} todayIso={ymd(todayDate())} />
                    {properties.map((p) => (
                        <PropertyRow
                            key={p.id}
                            property={p}
                            range={range}
                            todayIso={ymd(todayDate())}
                            events={eventsByProperty[p.id]}
                            onSelectEvent={setSelectedEvent}
                            days={days}
                            client={p.client_id ? clients.find((c) => c.id === p.client_id) : null}
                        />
                    ))}
                </div>
            )}

            {/* Event detail modal */}
            <Modal
                open={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                title={selectedEvent?.title || ""}
                subtitle={selectedEvent?.subtitle || ""}
                testId="cal-event-modal"
            >
                {selectedEvent && <EventDetail event={selectedEvent} properties={properties} />}
            </Modal>

            {/* Personal feed modal */}
            <PersonalFeedModal open={showFeedModal} onClose={() => setShowFeedModal(false)} />

            {/* Manual booking modal */}
            <BookingModal
                open={showBookingModal}
                onClose={() => setShowBookingModal(false)}
                properties={properties}
                onCreated={load}
            />
        </div>
    );
}

// ----------------------------------------------------------------------
// Avaibook-style calendar components
// ----------------------------------------------------------------------
const WEEKDAY_ES = ["D", "L", "M", "X", "J", "V", "S"]; // Domingo, Lunes, Martes, miércoles (X), Jueves, Viernes, Sábado
const LEFT_COL = 220;
const DAY_COL = 44; // narrower → more days visible
const ROW_HEIGHT = 44;

function ChannelIcon({ source, size = "md" }) {
    const s = size === "sm" ? 14 : size === "lg" ? 22 : 18;
    const fontSize = size === "sm" ? 8 : size === "lg" ? 12 : 10;
    const base = {
        width: s,
        height: s,
        fontSize,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontFamily: "Inter, sans-serif",
    };
    if (source === "airbnb") {
        return (
            <span
                style={{ ...base, background: "#FF5A5F", color: "#fff", borderRadius: "50%" }}
                title="Airbnb"
                data-testid={`channel-icon-${source}`}
            >
                A
            </span>
        );
    }
    if (source === "booking") {
        return (
            <span
                style={{ ...base, background: "#003580", color: "#fff", borderRadius: 3 }}
                title="Booking.com"
                data-testid={`channel-icon-${source}`}
            >
                B.
            </span>
        );
    }
    if (source === "vrbo") {
        return (
            <span
                style={{ ...base, background: "#3B5998", color: "#fff", borderRadius: 3 }}
                title="Vrbo"
                data-testid={`channel-icon-${source}`}
            >
                V
            </span>
        );
    }
    if (source === "avaibook") {
        return (
            <span
                style={{ ...base, background: "#0EA5E9", color: "#fff", borderRadius: 3 }}
                title="Avaibook"
                data-testid={`channel-icon-${source}`}
            >
                A
            </span>
        );
    }
    if (source === "avantio") {
        return (
            <span
                style={{ ...base, background: "#7C3AED", color: "#fff", borderRadius: 3 }}
                title="Avantio"
                data-testid={`channel-icon-${source}`}
            >
                Av
            </span>
        );
    }
    if (source === "manual") {
        return (
            <span
                style={{ ...base, background: "#9CA3AF", color: "#fff", borderRadius: "50%" }}
                title="Manual"
                data-testid={`channel-icon-${source}`}
            >
                <svg width={s - 4} height={s - 4} viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 22c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                </svg>
            </span>
        );
    }
    return (
        <span
            style={{ ...base, background: "#6F7470", color: "#fff", borderRadius: 3 }}
            title="Otro"
            data-testid={`channel-icon-${source}`}
        >
            ?
        </span>
    );
}

function CalendarHeader({ range, days, todayIso }) {
    // Group consecutive days by month for the top band
    const monthSegments = [];
    let curStart = 0;
    for (let i = 1; i <= range.length; i++) {
        const prev = range[i - 1];
        const cur = range[i];
        const isLast = i === range.length;
        const monthChanged = !isLast && cur && (cur.getUTCMonth() !== prev.getUTCMonth() || cur.getUTCFullYear() !== prev.getUTCFullYear());
        if (isLast || monthChanged) {
            const segStart = curStart;
            const segEnd = i - 1;
            monthSegments.push({
                start: segStart,
                span: segEnd - segStart + 1,
                label: prev.toLocaleDateString("es-ES", { month: "long", year: "numeric" }),
            });
            curStart = i;
        }
    }

    return (
        <div className="sticky top-0 z-20 bg-white border-b border-zentra-border" data-testid="calendar-header">
            {/* Month band */}
            <div
                className="grid border-b border-zentra-border"
                style={{
                    gridTemplateColumns: `${LEFT_COL}px repeat(${days}, ${DAY_COL}px)`,
                    minWidth: LEFT_COL + days * DAY_COL,
                }}
            >
                <div className="px-4 py-2 sticky left-0 bg-white z-10 border-r border-zentra-border">
                    <span className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">
                        Propiedad
                    </span>
                </div>
                {monthSegments.map((seg, i) => (
                    <div
                        key={i}
                        className="text-center py-2 border-l border-zentra-border bg-zentra-bg/40"
                        style={{ gridColumn: `span ${seg.span}` }}
                    >
                        <span className="font-display text-xs font-semibold text-zentra-ink capitalize tracking-wide">
                            {seg.label}
                        </span>
                    </div>
                ))}
            </div>
            {/* Weekday + day-number band */}
            <div
                className="grid"
                style={{
                    gridTemplateColumns: `${LEFT_COL}px repeat(${days}, ${DAY_COL}px)`,
                    minWidth: LEFT_COL + days * DAY_COL,
                }}
            >
                <div className="sticky left-0 bg-white z-10 border-r border-zentra-border" />
                {range.map((d) => {
                    const k = ymd(d);
                    const isToday = k === todayIso;
                    const isWeekend = [0, 6].includes(d.getUTCDay());
                    const dow = d.getUTCDay();
                    return (
                        <div
                            key={k}
                            className={`text-center py-1.5 border-l border-zentra-border relative ${
                                isWeekend ? "bg-zentra-bg/50" : "bg-white"
                            } ${isToday ? "!bg-sky-50" : ""}`}
                            data-testid={`cal-header-${k}`}
                        >
                            {isToday && (
                                <div
                                    className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-0 h-0"
                                    style={{
                                        borderLeft: "5px solid transparent",
                                        borderRight: "5px solid transparent",
                                        borderTop: "6px solid #2563EB",
                                    }}
                                />
                            )}
                            <div
                                className={`text-[9px] font-bold tracking-wide ${
                                    isToday ? "text-blue-700" : isWeekend ? "text-zentra-muted" : "text-zentra-muted"
                                }`}
                            >
                                {WEEKDAY_ES[dow]}
                            </div>
                            <div
                                className={`font-display text-sm leading-tight ${
                                    isToday ? "text-blue-700 font-bold" : "text-zentra-ink font-semibold"
                                }`}
                            >
                                {fmtDayNum(d)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function PropertyRow({ property, range, todayIso, events, onSelectEvent, days, client }) {
    const startTime = range[0].getTime();
    const ymdRange = range.map(ymd);
    const firstYmd = ymdRange[0];
    const lastYmd = ymdRange[ymdRange.length - 1];

    // Booking bars — Avaibook convention: checkout day is the LAST occupied day visually (half/half day)
    // We render bars from checkin column to checkout column, occupying full cells.
    const bookingBars = (events?.bookings || [])
        .map((b) => {
            const ci = b.checkin < firstYmd ? firstYmd : b.checkin;
            const co = b.checkout > lastYmd ? lastYmd : b.checkout;
            const ciIdx = Math.max(0, Math.round((parseYMD(ci).getTime() - startTime) / DAY_MS));
            const coIdx = Math.min(days - 1, Math.round((parseYMD(co).getTime() - startTime) / DAY_MS));
            if (coIdx < 0 || ciIdx > days - 1) return null;
            // Half-day offsets: checkin starts mid-day, checkout ends mid-day (Airbnb convention)
            const left = ciIdx * DAY_COL + DAY_COL * 0.5;
            const widthCols = coIdx - ciIdx;
            const width = Math.max(DAY_COL * 0.4, widthCols * DAY_COL);
            return { ...b, left, width };
        })
        .filter(Boolean);

    // Tasks NOT covered by a booking (e.g. manual deep cleans). Bookings already imply a cleaning task on checkout.
    const bookingCheckoutDays = new Set((events?.bookings || []).map((b) => b.checkout));
    const bookingCheckinDays = new Set((events?.bookings || []).map((b) => b.checkin));
    const looseTasks = (events?.tasks || []).filter((t) => !bookingCheckoutDays.has(t.scheduled_date));
    const looseTasksByDay = {};
    for (const t of looseTasks) {
        if (!looseTasksByDay[t.scheduled_date]) looseTasksByDay[t.scheduled_date] = [];
        looseTasksByDay[t.scheduled_date].push(t);
    }

    // Incidents (red dot in top-right of cell)
    const incidentsByDay = {};
    for (const i of events?.incidents || []) {
        const k = (i.created_at || "").slice(0, 10);
        if (!incidentsByDay[k]) incidentsByDay[k] = [];
        incidentsByDay[k].push(i);
    }

    return (
        <div
            className="grid border-b border-zentra-border hover:bg-zentra-bg/20 transition"
            style={{
                gridTemplateColumns: `${LEFT_COL}px repeat(${days}, ${DAY_COL}px)`,
                minWidth: LEFT_COL + days * DAY_COL,
                height: ROW_HEIGHT,
            }}
            data-testid={`cal-row-${property.code}`}
        >
            {/* Sticky property label */}
            <Link
                to={`/propiedades/${property.id}`}
                className="px-3 sticky left-0 z-10 bg-white border-r border-zentra-border flex items-center hover:bg-zentra-bg/40 transition"
                data-testid={`cal-prop-${property.code}`}
            >
                <div className="min-w-0">
                    <p className="font-display font-semibold text-[12px] text-zentra-ink uppercase leading-tight truncate tracking-wide">
                        {property.name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[9px] font-mono-zentra tracking-wider text-zentra-muted">
                            {property.code}
                        </span>
                        {client && (
                            <span
                                className="text-[9px] font-semibold tracking-wide px-1.5 py-0.5 rounded-sm bg-zentra-accent/10 text-zentra-accent-strong truncate max-w-[120px]"
                                title={client.name}
                                data-testid={`cal-prop-client-${property.code}`}
                            >
                                {client.name}
                            </span>
                        )}
                    </div>
                </div>
            </Link>
            {/* Day cells with background, weekends, today highlight */}
            <div className="relative col-span-full -ml-px"
                 style={{ gridColumn: `2 / span ${days}`, position: "relative", overflow: "hidden" }}
            >
                {/* Background cells */}
                <div
                    className="absolute inset-0 grid pointer-events-none"
                    style={{ gridTemplateColumns: `repeat(${days}, ${DAY_COL}px)` }}
                >
                    {range.map((d) => {
                        const k = ymd(d);
                        const isToday = k === todayIso;
                        const isWeekend = [0, 6].includes(d.getUTCDay());
                        return (
                            <div
                                key={k}
                                className={`border-l border-zentra-border ${
                                    isWeekend ? "bg-zentra-bg/40" : ""
                                } ${isToday ? "!bg-sky-50" : ""}`}
                            />
                        );
                    })}
                </div>

                {/* Booking bars */}
                {bookingBars.map((b) => {
                    const isBlock = b.source === "manual" || b.source === "block";
                    return (
                        <button
                            key={b.id}
                            type="button"
                            onClick={() =>
                                onSelectEvent({
                                    kind: "booking",
                                    ...b,
                                    title: `Reserva · ${b.guest_name || "(sin nombre)"}`,
                                    subtitle: `${SOURCE_LABEL[b.source] || b.source} · ${b.checkin} → ${b.checkout}`,
                                })
                            }
                            className="absolute flex items-center gap-1.5 px-1.5 rounded text-left transition hover:brightness-95 hover:shadow"
                            style={{
                                left: b.left,
                                width: b.width,
                                top: 8,
                                height: ROW_HEIGHT - 16,
                                background: isBlock ? "#E5E7EB" : "#A5D6C9",
                                color: isBlock ? "#374151" : "#0F4A3D",
                                border: "1px solid",
                                borderColor: isBlock ? "#D1D5DB" : "#5EBFB5",
                            }}
                            data-testid={`cal-booking-${b.id}`}
                        >
                            <ChannelIcon source={b.source} size="sm" />
                            <span className="text-[11px] font-semibold truncate flex-1">
                                {b.guest_name || "Huésped"}
                            </span>
                        </button>
                    );
                })}

                {/* Loose cleaning tasks (no booking) — small chip at top of cell */}
                <div
                    className="absolute inset-0 grid pointer-events-none"
                    style={{ gridTemplateColumns: `repeat(${days}, ${DAY_COL}px)` }}
                >
                    {range.map((d) => {
                        const k = ymd(d);
                        const dayTasks = looseTasksByDay[k] || [];
                        const dayInc = incidentsByDay[k] || [];
                        const isCheckout = bookingCheckoutDays.has(k);
                        const isCheckin = bookingCheckinDays.has(k);
                        if (dayTasks.length === 0 && dayInc.length === 0 && !isCheckout) return <div key={k} />;
                        return (
                            <div key={k} className="relative pointer-events-auto">
                                {/* SALIDA marker on the actual checkout day */}
                                {isCheckout && (
                                    <div
                                        className="absolute left-0.5 right-0.5 top-1 h-4 inline-flex items-center justify-center gap-0.5 rounded text-[9px] font-bold uppercase tracking-wider pointer-events-none"
                                        style={{
                                            background: "#FEE2E2",
                                            color: "#991B1B",
                                            border: "1px solid #FCA5A5",
                                            zIndex: 5,
                                        }}
                                        data-testid={`cal-checkout-${property.code}-${k}`}
                                        title={`Salida + limpieza · ${k}${isCheckin ? " · y entrada" : ""}`}
                                    >
                                        <LogOut size={8} />
                                        <span>Salida</span>
                                    </div>
                                )}
                                {dayTasks.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onSelectEvent({
                                                kind: "task",
                                                ...dayTasks[0],
                                                title: TASK_TYPE_LABELS[dayTasks[0].type] || dayTasks[0].type,
                                                subtitle: `${k} · ${property.code}`,
                                            })
                                        }
                                        className="absolute left-0.5 right-0.5 top-1 h-4 inline-flex items-center justify-center gap-0.5 rounded text-[9px] font-semibold transition hover:opacity-80 truncate"
                                        style={{
                                            background: "#FEF3C7",
                                            color: "#92400E",
                                            border: "1px solid #FDE68A",
                                        }}
                                        data-testid={`cal-task-${dayTasks[0].id}`}
                                    >
                                        <Sparkles size={8} />
                                        <span className="truncate">
                                            {dayTasks.length > 1 ? `${dayTasks.length}` : "Limpieza"}
                                        </span>
                                    </button>
                                )}
                                {dayInc.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onSelectEvent({
                                                kind: "incident",
                                                ...dayInc[0],
                                                title: dayInc[0].title,
                                                subtitle: `${INCIDENT_TYPE_LABELS[dayInc[0].type] || dayInc[0].type} · ${k}`,
                                            })
                                        }
                                        className="absolute top-1 right-1 w-2 h-2 rounded-full"
                                        style={{
                                            background:
                                                dayInc[0].priority === "critical"
                                                    ? "#DC2626"
                                                    : dayInc[0].priority === "high"
                                                      ? "#F97316"
                                                      : "#FBBF24",
                                        }}
                                        title={dayInc[0].title}
                                        data-testid={`cal-incident-${dayInc[0].id}`}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function EventDetail({ event, properties }) {
    const prop = properties.find((p) => p.id === event.property_id);
    if (event.kind === "booking") {
        return (
            <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                    <span
                        className="zentra-chip text-white border-transparent"
                        style={{ background: SOURCE_COLOR[event.source] || SOURCE_COLOR.other }}
                    >
                        {SOURCE_LABEL[event.source] || event.source}
                    </span>
                    {prop && (
                        <span className="zentra-chip bg-zentra-bg text-zentra-ink border-zentra-border">
                            {prop.code} · {prop.name}
                        </span>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Check-in" value={event.checkin} />
                    <Field label="Check-out" value={event.checkout} />
                </div>
                {event.notes && <p className="text-zentra-muted">{event.notes}</p>}
                {prop && (
                    <Link to={`/propiedades/${prop.id}`} className="text-zentra-accent-strong font-medium hover:underline">
                        Ver propiedad →
                    </Link>
                )}
            </div>
        );
    }
    if (event.kind === "task") {
        return (
            <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                    <StateBadge value={event.state} dict={TASK_STATE_LABELS} />
                    <StateBadge value={event.priority} dict={PRIORITY_LABELS} />
                </div>
                <Field label="Fecha" value={event.scheduled_date} />
                <Field label="Duración estimada" value={`${event.estimated_minutes} min`} />
                {event.notes && <p className="text-zentra-muted bg-zentra-bg/40 p-3 rounded-md">{event.notes}</p>}
                <Link to={`/limpiezas/${event.id}`} className="text-zentra-accent-strong font-medium hover:underline">
                    Abrir tarea →
                </Link>
            </div>
        );
    }
    if (event.kind === "incident") {
        return (
            <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                    <StateBadge value={event.priority} dict={PRIORITY_LABELS} />
                    <span className="zentra-chip bg-zentra-bg text-zentra-ink border-zentra-border">
                        {INCIDENT_TYPE_LABELS[event.type] || event.type}
                    </span>
                </div>
                {event.description && (
                    <p className="text-zentra-muted bg-zentra-bg/40 p-3 rounded-md">{event.description}</p>
                )}
                <Link to="/incidencias" className="text-zentra-accent-strong font-medium hover:underline">
                    Ver incidencias →
                </Link>
            </div>
        );
    }
    return null;
}

function Field({ label, value }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-widest text-zentra-muted font-semibold">{label}</p>
            <p className="text-zentra-ink font-medium mt-0.5">{value}</p>
        </div>
    );
}

function SchedulerChip({ scheduler, onRefresh }) {
    const [running, setRunning] = useState(false);

    const nextRunIn = (() => {
        if (!scheduler?.next_run) return null;
        const diff = new Date(scheduler.next_run).getTime() - Date.now();
        if (diff <= 0) return "ahora";
        const mins = Math.round(diff / 60000);
        if (mins < 60) return `en ${mins} min`;
        const hrs = Math.floor(mins / 60);
        return `en ${hrs}h ${mins % 60}m`;
    })();

    const lastRunAgo = (() => {
        const r = scheduler?.last_run;
        if (!r?.ran_at) return null;
        const diff = Date.now() - new Date(r.ran_at).getTime();
        const mins = Math.round(diff / 60000);
        if (mins < 1) return "hace un instante";
        if (mins < 60) return `hace ${mins} min`;
        const hrs = Math.floor(mins / 60);
        return `hace ${hrs}h ${mins % 60}m`;
    })();

    const runNow = async () => {
        setRunning(true);
        try {
            await api.post("/ical/run-now");
            await onRefresh?.();
        } finally {
            setRunning(false);
        }
    };

    if (!scheduler) {
        return null;
    }

    const enabled = scheduler.enabled;
    return (
        <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-zentra-border bg-white text-xs"
            data-testid="scheduler-chip"
            title={
                scheduler.last_run
                    ? `Última: ${lastRunAgo} · ${scheduler.last_run.properties_synced} props · ${scheduler.last_run.new_bookings} reservas · ${scheduler.last_run.tasks_created} tareas`
                    : "Aún no ha corrido"
            }
        >
            <span
                className={`w-1.5 h-1.5 rounded-full ${
                    enabled ? "bg-zentra-accent animate-pulse" : "bg-zentra-muted"
                }`}
            />
            <span className="text-zentra-muted font-medium tracking-wider uppercase text-[10px]">Auto-sync</span>
            <span className="text-zentra-ink font-semibold" data-testid="scheduler-next-run">
                {enabled ? nextRunIn || "—" : "off"}
            </span>
            {enabled && (
                <button
                    type="button"
                    onClick={runNow}
                    disabled={running}
                    className="ml-1 text-zentra-accent-strong hover:text-zentra-primary font-semibold"
                    data-testid="scheduler-run-now"
                    title="Ejecutar ahora"
                >
                    {running ? "…" : "ahora"}
                </button>
            )}
        </div>
    );
}

function PersonalFeedModal({ open, onClose }) {
    const [feedToken, setFeedToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/auth/feed-token")
            .then((r) => setFeedToken(r.data.feed_token))
            .finally(() => setLoading(false));
    }, [open]);

    const feedUrl = feedToken
        ? `${process.env.REACT_APP_BACKEND_URL}/api/calendar/feed/${feedToken}.ics`
        : "";

    const regen = async () => {
        if (!window.confirm("¿Regenerar el token? La URL anterior dejará de funcionar.")) return;
        setLoading(true);
        const { data } = await api.post("/auth/feed-token");
        setFeedToken(data.feed_token);
        setLoading(false);
    };

    const copy = async () => {
        if (!feedUrl) return;
        try {
            await navigator.clipboard.writeText(feedUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            /* ignore */
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Suscríbete a tu calendario ZENTRA"
            subtitle="Pega esta URL en Google Calendar, Apple Calendar o Outlook como calendario suscrito."
            wide
            testId="feed-modal"
            footer={
                <>
                    <button className="zentra-btn-danger" onClick={regen} disabled={loading}>
                        Regenerar token
                    </button>
                    <div className="flex-1" />
                    <button className="zentra-btn-primary" onClick={onClose}>
                        Hecho
                    </button>
                </>
            }
        >
            {loading ? (
                <div className="zentra-skeleton h-24" />
            ) : (
                <div className="space-y-4">
                    <div>
                        <label className="zentra-label">Tu URL personal (.ics)</label>
                        <div className="flex items-stretch gap-2">
                            <input
                                readOnly
                                value={feedUrl}
                                className="zentra-input font-mono-zentra text-xs"
                                onFocus={(e) => e.target.select()}
                                data-testid="feed-url-input"
                            />
                            <button
                                onClick={copy}
                                className="zentra-btn-accent whitespace-nowrap"
                                data-testid="feed-copy-button"
                            >
                                {copied ? "¡Copiado!" : "Copiar"}
                            </button>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3 text-xs">
                        <FeedHowTo
                            label="Google Calendar"
                            steps={[
                                "Configuración → Añadir calendario",
                                "Desde una URL",
                                "Pega la URL y guarda",
                            ]}
                        />
                        <FeedHowTo
                            label="Apple Calendar"
                            steps={[
                                "Archivo → Nueva suscripción",
                                "Pega la URL",
                                "Frecuencia: cada hora",
                            ]}
                        />
                        <FeedHowTo
                            label="Outlook"
                            steps={[
                                "Mi calendario → Añadir",
                                "Suscribirse desde web",
                                "Pega la URL y guarda",
                            ]}
                        />
                    </div>
                    <p className="text-xs text-zentra-muted">
                        Los clientes de calendario actualizan estas suscripciones cada 15 min – 6h según la app.
                        Verás tus tareas asignadas de los próximos 60 días + (si eres admin/coordinador) todas las reservas.
                    </p>
                </div>
            )}
        </Modal>
    );
}

function FeedHowTo({ label, steps }) {
    return (
        <div className="p-3 rounded-md border border-zentra-border bg-zentra-bg/40">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted mb-2">
                {label}
            </p>
            <ol className="text-zentra-ink space-y-1 list-decimal pl-4">
                {steps.map((s, i) => (
                    <li key={i}>{s}</li>
                ))}
            </ol>
        </div>
    );
}

function BookingModal({ open, onClose, properties, onCreated }) {
    const [form, setForm] = useState({
        property_id: "",
        checkin: ymd(todayDate()),
        checkout: ymd(new Date(todayDate().getTime() + 3 * DAY_MS)),
        guest_name: "",
        source: "manual",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setForm({
                property_id: "",
                checkin: ymd(todayDate()),
                checkout: ymd(new Date(todayDate().getTime() + 3 * DAY_MS)),
                guest_name: "",
                source: "manual",
                notes: "",
            });
            setError("");
        }
    }, [open]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.property_id) {
            setError("Selecciona una propiedad");
            return;
        }
        if (form.checkin >= form.checkout) {
            setError("El check-out debe ser posterior al check-in");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await api.post("/bookings", form);
            onClose();
            onCreated?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Reserva manual"
            subtitle="Para reservas directas no provenientes de Airbnb / Booking. Se generará automáticamente la tarea de limpieza para el check-out."
            wide
            testId="booking-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="booking-form"
                        className="zentra-btn-primary"
                        disabled={saving}
                        data-testid="save-booking"
                    >
                        {saving ? "Guardando…" : "Crear reserva"}
                    </button>
                </>
            }
        >
            <form id="booking-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                    <label className="zentra-label">Propiedad</label>
                    <select
                        className="zentra-select"
                        required
                        value={form.property_id}
                        onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                        data-testid="booking-property"
                    >
                        <option value="">Seleccionar propiedad…</option>
                        {properties.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.code} · {p.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="zentra-label">Check-in</label>
                    <input
                        type="date"
                        className="zentra-input"
                        required
                        value={form.checkin}
                        onChange={(e) => setForm({ ...form, checkin: e.target.value })}
                        data-testid="booking-checkin"
                    />
                </div>
                <div>
                    <label className="zentra-label">Check-out</label>
                    <input
                        type="date"
                        className="zentra-input"
                        required
                        value={form.checkout}
                        onChange={(e) => setForm({ ...form, checkout: e.target.value })}
                        data-testid="booking-checkout"
                    />
                </div>
                <div>
                    <label className="zentra-label">Huésped</label>
                    <input
                        className="zentra-input"
                        value={form.guest_name}
                        onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                        placeholder="Familia Pérez"
                        data-testid="booking-guest"
                    />
                </div>
                <div>
                    <label className="zentra-label">Origen</label>
                    <select
                        className="zentra-select"
                        value={form.source}
                        onChange={(e) => setForm({ ...form, source: e.target.value })}
                        data-testid="booking-source"
                    >
                        <option value="manual">Manual</option>
                        <option value="airbnb">Airbnb</option>
                        <option value="booking">Booking.com</option>
                        <option value="vrbo">Vrbo</option>
                        <option value="avaibook">Avaibook</option>
                        <option value="avantio">Avantio</option>
                        <option value="other">Otro</option>
                    </select>
                </div>
                <div className="col-span-2">
                    <label className="zentra-label">Notas</label>
                    <textarea
                        className="zentra-textarea min-h-[60px]"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                </div>
                {error && (
                    <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                        {error}
                    </div>
                )}
            </form>
        </Modal>
    );
}
